import React from 'react';
import EmailLink from './EmailLink';
import PhoneLink from './PhoneLink';
import { Floor, Hostel, Invoice, Room, User } from '../types';
import { FloorRoomLookup, formatFloorRoomBedLine } from '../utils/locationLabels';
import {
  formatGuestMoney,
  guestInvoiceSummary,
  GuestStayContext,
  isGuestStayInvoice,
} from '../utils/guestInvoiceDisplay';
import './InvoicePdfDocument.css';

export function formatInvoicePdfDate(dateString: string | undefined | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '—';
  const day = date.getUTCDate().toString().padStart(2, '0');
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}-${month}-${year}`;
}

export function invoicePdfNumber(id: number): string {
  return `#00000${id}`;
}

function formatMoney(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function hostelAddressLines(hostel: Hostel | null | undefined): string[] {
  if (!hostel) return [];
  const lines: string[] = [];
  if (hostel.address?.trim()) lines.push(hostel.address.trim());
  const cityLine = [hostel.city, hostel.state, hostel.pincode].filter(Boolean).join(', ');
  if (cityLine) lines.push(cityLine);
  if (hostel.country?.trim() && hostel.country.trim() !== 'India') lines.push(hostel.country.trim());
  return lines;
}

export type InvoicePdfDocumentProps = {
  invoice: Invoice;
  tenant?: User | null;
  hostel?: Hostel | null;
  locationLookup?: FloorRoomLookup;
  guestStay?: GuestStayContext | null;
};

export const InvoicePdfDocument: React.FC<InvoicePdfDocumentProps> = ({
  invoice,
  tenant,
  hostel,
  locationLookup,
  guestStay: guestStayContext,
}) => {
  const floorRoomBed = formatFloorRoomBedLine(
    {
      floor: invoice.floor ?? tenant?.floor,
      room: invoice.room ?? tenant?.room,
      bed: invoice.bed ?? tenant?.bed,
    },
    locationLookup
  );
  const isGuestStay = isGuestStayInvoice(invoice);
  const guestSum = isGuestStay ? guestInvoiceSummary(invoice, guestStayContext) : null;
  const paid = parseFloat(invoice.paidAmount || '0') || 0;
  const balance = parseFloat(invoice.balanceAmount || '0') || 0;
  const discount = parseFloat(invoice.discount || '0') || 0;
  const lineTotal = guestSum ? guestSum.totalDue : paid + balance + discount;
  const hasBalance = balance !== 0;
  const hostelName = hostel?.name?.trim() || 'Hostel';
  const addressLines = hostelAddressLines(hostel);
  const billAddress = tenant?.address?.trim() || 'N/A';
  const dueDateLabel = invoice.dueDate ? formatInvoicePdfDate(invoice.dueDate) : '—';
  const dueDay = hostel?.billingDueDay != null ? Number(hostel.billingDueDay) : 5;

  return (
    <article className="inv-pdf-page" aria-label={`Invoice ${invoicePdfNumber(invoice.id)}`}>
      <h1 className="inv-pdf-title">INVOICE</h1>

      <header className="inv-pdf-header">
        <div className="inv-pdf-logo-wrap">
          {hostel?.logo ? (
            <img src={hostel.logo} alt="" className="inv-pdf-logo" />
          ) : (
            <div className="inv-pdf-logo-placeholder" aria-hidden>
              {hostelName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="inv-pdf-company">
          <p className="inv-pdf-company-name">{hostelName}</p>
          {addressLines.map((line) => (
            <p key={line} className="inv-pdf-company-line">
              {line}
            </p>
          ))}
          {hostel?.phone?.trim() ? (
            <p className="inv-pdf-company-line">
              Phone: <PhoneLink phone={hostel.phone} className="inv-pdf-contact-link" fallback="" />
            </p>
          ) : null}
          {hostel?.email?.trim() ? (
            <p className="inv-pdf-company-line">
              <EmailLink email={hostel.email} className="inv-pdf-contact-link" fallback="" />
            </p>
          ) : null}
          <p className="inv-pdf-invoice-no">Invoice No: {invoicePdfNumber(invoice.id)}</p>
        </div>
      </header>

      <section className="inv-pdf-bill-to">
        <p className="inv-pdf-section-label">Bill To:</p>
        <p className="inv-pdf-bill-name">{invoice.name || tenant?.username || 'N/A'}</p>
        <p>{billAddress}</p>
        <p>
          <PhoneLink phone={invoice.phoneNumber || tenant?.phoneNumber} fallback="N/A" className="inv-pdf-contact-link" />
        </p>
        <p>Floor-Room-Bed: {floorRoomBed}</p>
      </section>

      <section className="inv-pdf-meta">
        <p>
          <span className="inv-pdf-meta-label">Invoice Date:</span> {formatInvoicePdfDate(invoice.paymentTimestamp)}
        </p>
        <p>
          <span className="inv-pdf-meta-label">Invoice Type:</span>{' '}
          {isGuestStay ? 'Guest stay (daily)' : invoice.paymentType || '—'}
        </p>
        {isGuestStay && guestSum ? (
          <>
            <p>
              <span className="inv-pdf-meta-label">Daily rate:</span> {formatGuestMoney(guestSum.dailyRate)}
            </p>
            <p>
              <span className="inv-pdf-meta-label">Number of days:</span> {guestSum.days}
            </p>
          </>
        ) : (
          <p>
            <span className="inv-pdf-meta-label">Month/Year:</span> {invoice.paymentMonth}/{invoice.paymentYear}
          </p>
        )}
      </section>

      <table className="inv-pdf-table">
        <thead>
          <tr>
            <th>Item &amp; Description</th>
            <th>Amount</th>
          </tr>
        </thead>
        <tbody>
          {isGuestStay && guestSum ? (
            <tr>
              <td>
                Daily charge ({formatGuestMoney(guestSum.dailyRate)}/day × {guestSum.days} day
                {guestSum.days === 1 ? '' : 's'})
              </td>
              <td>{formatGuestMoney(guestSum.totalDue)}</td>
            </tr>
          ) : (
            <tr>
              <td>Monthly Charges</td>
              <td>{formatMoney(lineTotal)}</td>
            </tr>
          )}
          {!isGuestStay && hasBalance ? (
            <tr>
              <td>
                <span className="inv-pdf-line-main">Due Amount</span>
                <span className="inv-pdf-line-sub">Due Date: {dueDateLabel}</span>
              </td>
              <td className="inv-pdf-amount-cell">{formatMoney(balance)}</td>
            </tr>
          ) : null}
        </tbody>
      </table>

      <section className="inv-pdf-summary">
        {isGuestStay && guestSum ? (
          <>
            <div className="inv-pdf-summary-row">
              <span>Stay total</span>
              <span>{formatGuestMoney(guestSum.totalDue)}</span>
            </div>
            <div className="inv-pdf-summary-row">
              <span>Paid (this payment)</span>
              <span>{formatGuestMoney(guestSum.paid)}</span>
            </div>
            <div className="inv-pdf-summary-row inv-pdf-summary-row--due">
              <span>Balance remaining</span>
              <span>{formatGuestMoney(guestSum.balance)}</span>
            </div>
          </>
        ) : (
          <>
            <div className="inv-pdf-summary-row">
              <span>Total</span>
              <span>{formatMoney(lineTotal)}</span>
            </div>
            <div className="inv-pdf-summary-row">
              <span>Discount</span>
              <span>{discount > 0 ? formatMoney(discount) : 'N/A'}</span>
            </div>
            <div className="inv-pdf-summary-row">
              <span>Paid</span>
              <span>{paid > 0 ? formatMoney(paid) : 'N/A'}</span>
            </div>
            <div className="inv-pdf-summary-row inv-pdf-summary-row--due">
              <span>Balance Due</span>
              <span className="inv-pdf-summary-due-block">
                <span>{hasBalance ? formatMoney(balance) : 'N/A'}</span>
                {hasBalance && dueDateLabel !== '—' ? (
                  <span className="inv-pdf-summary-due-date">Due Date: {dueDateLabel}</span>
                ) : null}
              </span>
            </div>
          </>
        )}
      </section>

      <footer className="inv-pdf-footer">
        <p className="inv-pdf-thanks">Thank you!</p>
        <p className="inv-pdf-terms-title">Terms &amp; Conditions</p>
        <p className="inv-pdf-terms">
          {isGuestStay
            ? 'Daily guest stay charges. Please pay any remaining balance before check-out.'
            : `Please pay your hostel fee on or before ${dueDay}th of every month to avoid penalty.`}
        </p>
        <p className="inv-pdf-terms">This is computer generated invoice, no signature needed.</p>
        {hostel?.phone?.trim() ? (
          <p className="inv-pdf-terms">
            Phone: <PhoneLink phone={hostel.phone} className="inv-pdf-contact-link" fallback="" />
          </p>
        ) : null}
        {hostel?.email?.trim() ? (
          <p className="inv-pdf-terms">
            Email: <EmailLink email={hostel.email} className="inv-pdf-contact-link" fallback="" />
          </p>
        ) : null}
      </footer>
    </article>
  );
};
